/*
  Recently viewed — the list, and the cards built from it.

  Figma 405:128. Two jobs that happen on different pages, which is why one
  file does both: every product page WRITES its handle to the list, and the
  empty cart drawer READS the list and renders cards from it.

  WHERE THE LIST LIVES

  localStorage, not the cart and not a customer record. It is per browser and
  per device on purpose — it is a memory aid, not data worth keeping, and
  storing it server-side would mean writing to the customer object on every
  product view for something nobody would miss.

  It can throw rather than return null: a private window, site data blocked,
  or a thumbnail capture. Every read and write is wrapped, and a failure means
  the strip simply does not appear.
*/
(() => {
  const KEY = 'tyo:recently-viewed';
  const KEEP = 20; // remembered; the drawer shows far fewer

  const read = () => {
    try {
      const raw = localStorage.getItem(KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter((h) => typeof h === 'string') : [];
    } catch {
      return [];
    }
  };

  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, KEEP)));
    } catch {
      /* storage unavailable — the feature is optional, so this is not an error */
    }
  };

  /* ------------------------------------------------------------- record */

  /*
    The handle comes from a data attribute the drawer writes on every product
    page, rather than from parsing location.pathname — a product can be
    reached through a collection URL (/collections/diwali/products/handle) and
    through /products/handle, and the last path segment is only right by
    accident in the first case.
  */
  const record = () => {
    const handle = document.body.dataset.tyoProductHandle;
    if (!handle) return;

    const list = read().filter((h) => h !== handle);
    list.unshift(handle);
    write(list);
  };

  /* ------------------------------------------------------------- render */

  const cardUrl = (handle) =>
    `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(
      handle
    )}?section_id=recently-viewed-card`;

  /*
    Cards are fetched in parallel but WRITTEN in list order. Promise.all keeps
    the order of its input regardless of which request finishes first, so the
    strip does not shuffle itself depending on the network.

    A product that has been deleted or unpublished 404s. That card is dropped
    and the rest still render — a stale handle should not empty the strip.
  */
  async function render(root) {
    const limit = parseInt(root.dataset.limit, 10) || 5;
    const current = document.body.dataset.tyoProductHandle;

    // Looking at a product and opening the bag should not offer that same
    // product back.
    const handles = read()
      .filter((h) => h !== current)
      .slice(0, limit);

    if (!handles.length) return;

    const rail = root.querySelector('[data-rv-rail]');
    if (!rail) return;

    const results = await Promise.all(
      handles.map((handle) =>
        fetch(cardUrl(handle))
          .then((res) => (res.ok ? res.text() : null))
          .catch(() => null)
      )
    );

    /*
      THE WRAPPER HAS TO COME OFF.

      ?section_id= always returns the section inside

          <div id="shopify-section-recently-viewed-card" class="shopify-section">

      Keep it and that div, not .tyo-rv__card, is the rail's flex item — so the
      card width and the gaps apply to nothing and the strip collapses. And
      every one of the five carries the SAME element id, which is invalid and
      makes anything that looks a card up by id find the first one every time.

      So the card is lifted out of the wrapper and the wrapper is thrown away.
    */
    const frag = document.createDocumentFragment();
    results.forEach((html) => {
      if (!html) return;
      const holder = document.createElement('div');
      holder.innerHTML = html;
      const card = holder.querySelector('.tyo-rv__card');
      if (card) frag.appendChild(card);
    });

    if (!frag.childElementCount) return;

    rail.replaceChildren(frag);
    root.hidden = false;
  }

  /* -------------------------------------------------------- add to cart */

  /*
    Adds through the drawer rather than the page, so the bag fills in place and
    the empty state is replaced by the real one. The sections list comes from
    the drawer itself, so this stays correct if the drawer's sections change.
  */
  async function addToCart(button) {
    const id = button.dataset.variantId;
    const drawer = document.querySelector('cart-drawer');
    if (!id || !drawer) return;

    button.classList.add('is-loading');
    button.disabled = true;

    const sections = drawer.getSectionsToRender
      ? drawer.getSectionsToRender().map((section) => section.id)
      : [];

    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(id), quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const state = await res.json();

      if (state.status) {
        // Shopify answers an error with a status field. Nothing has been added.
        button.classList.remove('is-loading');
        button.disabled = false;
        return;
      }

      drawer.renderContents(state);
    } catch {
      button.classList.remove('is-loading');
      button.disabled = false;
    }
  }

  /* ---------------------------------------------------------------- run */

  record();

  /*
    Delegated, and bound once. The drawer replaces its own innerHTML whenever
    the cart changes, so anything bound directly to a card button stops working
    the first time somebody adds something.
  */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-rv-add]');
    if (button) addToCart(button);
  });

  /*
    The strip is inside the drawer's empty state, which is re-rendered on every
    cart change — so it is looked for again each time rather than once at load.
  */
  const hydrate = () => {
    document.querySelectorAll('[data-rv]:not([data-rv-done])').forEach((root) => {
      root.dataset.rvDone = 'true';
      render(root);
    });
  };

  hydrate();
  document.addEventListener('cart:refresh', hydrate);
  new MutationObserver(hydrate).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
